<template>
  <div class="d-flex justify-content-start align-items-center dashboard-title-wrap">
    <a
      v-if="!backButtonHiddenRoleIds.includes(String(rmData?.RoleId ?? ''))"
      class="btn-back cursor-pointer"
      @click="goBack()"
    >
      <img src="~@/assets/images/savings-back.png" alt="" />
    </a>
    <h5>Dashboard</h5>
  </div>

  <Toolbar class="p-tool dashboard-header-prime-toolbar">
    <template #start>
      <div class="dashboard-header-toolbar-start">
        <FloatLabel v-if="isDesktop" variant="on" class="dashboard-header-toolbar-start__block">
          <MultiSelect
            id="selected_column_label"
            :modelValue="localSelectedColumns"
            :options="allColumns"
            optionLabel="header"
            filter
            :maxSelectedLabels="3"
            class="multi w-full md:w-80"
            display="chip"
            placeholder="Select Columns"
            size="small"
            inputId="selected-Columns"
            @update:modelValue="onToggle"
          />
          <label v-if="localSelectedColumns.length !== 0" for="selected-Columns">Columns</label>
        </FloatLabel>

        <FloatLabel
          v-if="localFilters && localFilters['global']"
          variant="on"
          class="dashboard-header-toolbar-start__block"
        >
          <IconField id="on_label" class="search-container dashboard-header-quick-search">
            <InputIcon><i class="pi pi-search" /></InputIcon>
            <InputText
              :modelValue="localFilters['global'].value"
              @update:modelValue="onQuickSearchInput"
              @paste="onQuickSearchPaste"
              size="small"
              class="w-full md:w-80"
            />
          </IconField>
          <label for="on_label" style="font-size:12px">Quick Search</label>
        </FloatLabel>
      </div>
    </template>

    <template #end>
      <div class="dashboard-header-toolbar-end">
        <SelectButton
          v-if="showCategorySelect"
          v-model="categoryValue"
          :options="categoryOptions"
          :disabled="isCategoryDisabled"
          aria-labelledby="basic"
          optionLabel="name"
          optionValue="value"
          @change="handleChange"
        />
        <div v-if="isUserActionsVisible" class="dashboard-header-toolbar-end__item">
          <SplitButton
            label="User Assign"
            :disabled="!tableData"
            :model="assignActions"
            icon="pi pi-user"
            size="small"
          />
        </div>
        <div class="button-filter dashboard-header-toolbar-end__item">
          <Button
            v-tooltip.top="'Reset filters'"
            type="button"
            icon="pi pi-filter-slash"
            outlined
            size="small"
            class="dashboard-header-action-btn"
            aria-label="Reset filters"
            @click="clearFilter"
          />
        </div>
        <div v-if="isDesktop && isExportCsv" class="button-filter dashboard-header-toolbar-end__item">
          <Button
            v-tooltip.top="'Export to CSV'"
            icon="pi pi-external-link"
            outlined
            size="small"
            class="dashboard-header-action-btn"
            aria-label="Export to CSV"
            @click="exportCSV($event)"
          />
        </div>
        <div v-if="showRmFileUpload" class="button-filter dashboard-header-toolbar-end__item">
          <Button
            v-tooltip.top="'SP Data Management'"
            type="button"
            icon="pi pi-user"
            outlined
            size="small"
            @click="fileUpload"
          />
        </div>
      </div>
    </template>
  </Toolbar>
</template>

<script>
import {
  Toolbar,
  Button,
  SplitButton,
  FloatLabel,
  IconField,
  InputText,
  InputIcon,
  MultiSelect,
  SelectButton,
} from 'primevue';
import Tooltip from 'primevue/tooltip';
import { ROLE_ID } from '@/constants/roles';
import { stripHtml } from '@/utils/inputSanitizer';

export default {
  name: 'DashboardHeader',
  components: {
    Toolbar,
    Button,
    SplitButton,
    FloatLabel,
    IconField,
    InputText,
    InputIcon,
    MultiSelect,
    SelectButton,
  },
  // PrimeVue Tooltip — global `v-tooltip` package does not reliably bind to PrimeVue 4 controls.
  directives: {
    tooltip: Tooltip,
  },
  props: {
    filters: { type: Object, required: true },
    selectedColumns: { type: Array, required: true },
    allColumns: { type: Array, required: true },
    assignActions: { type: Array, required: true },
    rmData: { type: Object, required: false, default: null },
    distributorID: { type: String, required: false, default: '' },
    isUserActionsVisible: { type: Boolean, required: true },
    isDesktop: { type: Boolean, default: true },
    tableData: { type: Array, required: true },
    isExportCsv: { type: Boolean, default: false },
  },
  data() {
    return {
      localFilters: this.filters,
      localSelectedColumns: this.selectedColumns,
      categoryValue: '',
      categoryOptions: [],
      backButtonHiddenRoleIds: [
        ROLE_ID.ADMIN,
        ROLE_ID.TELE_OPERATOR,
        ROLE_ID.ROLE_7,
        ROLE_ID.ROLE_8,
        ROLE_ID.AV,
        ROLE_ID.TELE_MARKETER,
      ],
    };
  },
  computed: {
    showCategorySelect() {
      return Array.isArray(this.categoryOptions) && this.categoryOptions.length > 1;
    },
    isCategoryDisabled() {
      return !this.categoryOptions || this.categoryOptions.length <= 1;
    },
    showRmFileUpload() {
      return String(this.rmData?.RoleId ?? '') === ROLE_ID.ADMIN;
    },
  },
  watch: {
    filters(val) {
      this.localFilters = val;
    },
    selectedColumns(val) {
      this.localSelectedColumns = val;
    },
    localFilters(val) {
      this.$emit('update:filters', val);
    },
    localSelectedColumns(val) {
      this.$emit('update:selectedColumns', val);
    },
  },
  mounted() {
    const cfg = this.$store.state.configData;
    this.categoryOptions =
      cfg?.MOTOR_PRODUCT_TYPE ||
      cfg?.MOTOR_DASHBOARD_PRODUCT_TYPE ||
      cfg?.HEALTH_PRODUCT_TYPE ||
      [];
    if (Array.isArray(this.categoryOptions) && this.categoryOptions.length > 0) {
      if (sessionStorage.getItem('productType')) {
        try {
          this.categoryValue = JSON.parse(sessionStorage.getItem('productType'));
        } catch {
          this.categoryValue = this.categoryOptions[0]?.value ?? '';
        }
      } else {
        this.categoryValue = this.categoryOptions[0]?.value ?? '';
      }
    }
  },
  methods: {
    onToggle(value) {
      this.localSelectedColumns = this.allColumns.filter((col) =>
        value?.some((selCol) => selCol.field === col.field)
      );
    },
    clearFilter() {
      this.$emit('clear-filters');
    },
    exportCSV(event) {
      this.$emit('export-csv', event);
    },
    fileUpload() {
      this.$emit('file-upload');
    },
    handleChange($event) {
      this.categoryValue = $event.value;
      this.$emit('handle-category-filter', $event);
    },
    goBack() {
      this.$router.push('/');
    },
    onQuickSearchInput(value) {
      if (!this.localFilters?.global) return;
      this.localFilters.global.value = stripHtml(value ?? '');
    },
    onQuickSearchPaste(event) {
      event.preventDefault();
      if (!this.localFilters?.global) return;
      const pasted = (event.clipboardData || window.clipboardData).getData('text');
      const current = this.localFilters.global.value ?? '';
      this.localFilters.global.value = stripHtml(current + pasted);
    },
  },
};
</script>

<style scoped>
/* Toolbar root: `p-toolbar` + `p-tool` are the same node — style that element directly */
.dashboard-header-prime-toolbar.p-toolbar {
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  min-height: 3rem;
}

.dashboard-header-prime-toolbar :deep(.p-toolbar-start),
.dashboard-header-prime-toolbar :deep(.p-toolbar-end) {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}

/* Space column picker and quick search (Prime Toolbar start slot lays children flush). */
.dashboard-header-toolbar-start {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem 1.25rem;
  min-height: 2.75rem;
  padding-bottom: 0.125rem;
}

.dashboard-header-toolbar-start__block {
  flex: 0 1 auto;
  min-width: 0;
}

/* Match control heights so the start row doesn’t clip FloatLabel */
.dashboard-header-toolbar-start__block :deep(.p-multiselect.p-inputfield-sm) {
  min-height: 2.375rem;
  align-items: center;
}

/* End slot: explicit gap between icon-only outlined buttons (they were 32px flush) */
.dashboard-header-toolbar-end {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}

.dashboard-header-toolbar-end__item {
  display: inline-flex;
  align-items: center;
}

/* Align Quick Search with MultiSelect small: same Prime `p-inputfield-sm` scale + stable line-height. */
.dashboard-header-toolbar-start :deep(.dashboard-header-quick-search.p-iconfield .p-inputtext.p-inputfield-sm) {
  box-sizing: border-box;
  line-height: 1.25;
  min-height: 2.375rem;
}
</style>
